import { Component, OnInit, OnDestroy } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { MainFacade } from '../../../main.facade';
import { IFileInfo } from '../../../models/file-info';
import { FormBuilder, Validators } from '@angular/forms';
import { RequestFactory } from '../../../models/request.factory';
import { Subscription } from 'rxjs';
import { SubmissionStateService } from '../../../../services/submission-state.service';
import { ProcessComponentBase } from '../process.component.base';

import {
  StainAdapterChannelMode,
  StainAdapterMode,
  StainAdapterModel,
  StainAdapterNormalizeMethod,
  StainAdapterNormalizeTarget,
} from '../../../models/request';

@Component({
  selector: 'stain-adapter',
  templateUrl: './stain-adapter.component.html',
  styleUrls: ['./stain-adapter.component.scss'],
})
export class StainAdapterComponent extends ProcessComponentBase implements OnInit {

  protected readonly StainAdapterNormalizeMethod = StainAdapterNormalizeMethod;

  normMethods = Object.values(StainAdapterNormalizeMethod);
  normTargets = Object.values(StainAdapterNormalizeTarget);
  ganModels = Object.values(StainAdapterModel);
  modes = Object.values(StainAdapterMode);
  channelModes = Object.values(StainAdapterChannelMode);
  patchSizes = [512, 256];
  selectedModel: string = StainAdapterModel.GR2RGB_1GRAY_2ZEROS_E23_BATCHNORM_BS1_X20;

  public inputImages!: IFileInfo[];


  constructor(mainFacade: MainFacade,
              private fb: FormBuilder		,
							submissionState: SubmissionStateService) {
								
		super('Stain Adapter', submissionState, mainFacade);
	}

  ngOnInit(): void {
    this.configForm();
    this.mainFacade.getSelectedNode$().subscribe({
      next: (node) => {
        if (node == undefined || node.data == undefined) {
          this.bucket = undefined;
        } else {
          this.setImagesInput(node);
        }
      },
      error: (err) => {
        console.error(`Error with node selection: ${err}`);
      },
    });
    this.mainFacade.getValidationMessage$().subscribe({
      next: (message) => {
        this.validationMessage = message;
      }
    });
  }

	setImagesInput(node: TreeNode<IFileInfo>) {
	  this.inputImages = [];
	  // if folder
	  if (!node.leaf) {
	    node.children?.forEach(child => {
	      if (child.data && this.isImageExtension(child.data.name)) {
	        this.inputImages.push(child.data);
	      }
	    });
	  } else {
	    // if file
	    if (node.data && this.isImageExtension(node.data.name)) {
	      this.inputImages.push(node.data);
	    }
	  }
	  this.bucket = node.data?.project.bucket;
	  this.path = node.parent?.data?.relPath;
	}

  onModelChange(event: any) {
    this.selectedModel = event.value;
    // set the channelMode and mode based on the model
    this.form.get('channelMode')?.setValue(this.getModelChannelMode(this.selectedModel));
    this.form.get('mode')?.setValue(this.getModelMode(this.selectedModel));
    this.form.get('patchSize')?.setValue(this.getModelPatchSize(this.selectedModel));
  }
  onNormMethodChange(event: any) {
    const normMethod = event.value;
    if (normMethod === StainAdapterNormalizeMethod.NONE) {
      this.form.get('normTargetPath')?.disable({ emitEvent: false });
      this.form.get('saveNormalization')?.disable({ emitEvent: false });
    } else {
      this.form.get('normTargetPath')?.enable({ emitEvent: false });
      this.form.get('saveNormalization')?.enable({ emitEvent: false });
    }
  }

  getModelPatchSize(modelName: string): number {
    if (modelName.includes('x20')) {
      return 256;
    }
    return 512
  }

  getModelMode(modelName: string): StainAdapterMode {
    if (modelName.includes('gr2rgb')) {
      return StainAdapterMode.GR2RGB;
    } else if (modelName.includes('lab')) {
      return StainAdapterMode.LAB;
    } else {
      return StainAdapterMode.LAB;
    }
    return StainAdapterMode.LAB; // default
  }
  getModelChannelMode(modelName: string): StainAdapterChannelMode {
    if (modelName.includes('1gray-2zeros')) {
      return StainAdapterChannelMode.GR_2EMPTY;
    } else if (modelName.includes('3grays')) {
      return StainAdapterChannelMode.TRIPLE_GRAY;
    } else if (modelName.includes('2L')) {
      return StainAdapterChannelMode.DOUBLE_L;
    } else if (modelName.includes('l-empty')) {
      return StainAdapterChannelMode.L_EMPTY;
    } else {
      return StainAdapterChannelMode.DEFAULT; // default
    }
  }

  isImageExtension(name: string) {
    const extension = name.toLowerCase().split('.').pop();
    return extension === 'tif' || extension === 'tiff' || extension === 'png' || extension === 'jpg' || extension === 'jpeg' || extension === 'ndpi';
  }
  configForm() {
    this.form = this.fb.group({
      saveNormalization: [{ value: true, disabled: true }],
      normMethod: [StainAdapterNormalizeMethod.NONE, Validators.required],
      normTargetPath: [{ value: StainAdapterNormalizeTarget.SMALL_RES_VAHA_MAC, disabled: true },
        Validators.required],
      ganModelPath: [StainAdapterModel.GR2RGB_1GRAY_2ZEROS_E23_BATCHNORM_BS1_X20, Validators.required],
      mode: [StainAdapterMode.LAB, Validators.required],
      patchSize: [512, Validators.required],
      channelMode: [StainAdapterChannelMode.DEFAULT, Validators.required],
      outputFolder: [null, Validators.required],
      editOutputFolder: [false],
      normOnly: [false],
    });
    this.persistForm();
  }

  onSubmit() {
    super.submit(this.buildRequest());
  }

  onCreateRule() {
    super.createRule(this.buildRequest());
  }

  private buildRequest() {
    const formValue = this.form.value;
    const bucket = this.bucket;
    const parentPath = this.path;
		return RequestFactory.getStainAdapterRequestFromForm(formValue, bucket,
		  parentPath, this.inputImages);
  }

}